import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from './api';
import {
  CATEGORIAS, agruparCursos, estadoTurma, historicoRecente, negocioMaisRecente,
  nomeVendedor, podeEstenderPrazo, podeOrcar, prazoContato, temNegocio, temSolicitacaoAprovada,
  turmasLivresParaRelacionar, turmasParaSolicitar
} from './regras';
import { DialogoRelacionar } from './Dialogos';

const LINK_BITRIX = 'https://alphae.bitrix24.com.br/crm/deal/details/';

function classeDoStatus(turma) {
  const categoria = turma.CategoriaStatus && CATEGORIAS[turma.CategoriaStatus];
  if (categoria) return categoria.classe;
  return turma.Status ? 'nm' : 'vazio';
}

function rotuloDoStatus(turma) {
  const categoria = turma.CategoriaStatus && CATEGORIAS[turma.CategoriaStatus];
  if (categoria) return categoria.rotulo;
  return turma.Status || 'Sem negócio';
}

// Situação da turma para o usuário: botões quando há o que fazer, nota quando é só informação
function AcaoDaTurma({ estado, curso, turma, onSolicitar, onAvaliar, onProrrogar }) {
  switch (estado.tipo) {
    case 'avaliar':
      return <button type="button" className="cx-btn cx-mini cx-aviso" onClick={() => onAvaliar(estado.solicitacao)}>Avaliar solicitação</button>;
    case 'prioridade':
      return <span className="cx-nota cx-nota-ok">Prioridade sua</span>;
    case 'negociando':
      return <span className="cx-nota cx-nota-neg">Em negociação com você</span>;
    case 'solicitado':
      return <span className="cx-selo cx-aviso">Solicitado · em análise</span>;
    case 'analise':
      return <span className="cx-selo cx-aviso">Em análise</span>;
    case 'prazo':
      return (
        <React.Fragment>
          <span className={`cx-selo ${estado.dias <= 2 ? 'cx-aviso' : 'cx-fec'}`}>
            Prioridade · {estado.dias < 0 ? 'vencida' : `${estado.dias} ${estado.dias === 1 ? 'dia' : 'dias'}`}
          </span>
          {estado.podeProrrogar && (
            <button type="button" className="cx-btn cx-mini" onClick={() => onProrrogar(estado.solicitacao)}>Prorrogar +5d</button>
          )}
        </React.Fragment>
      );
    case 'prioridadeOutro':
      return <span className="cx-nota">Prioridade de outro vendedor</span>;
    case 'negOutro':
      return <span className="cx-nota">Em negociação com outro vendedor</span>;
    case 'solicitar':
      return <button type="button" className="cx-btn cx-mini" onClick={() => onSolicitar(curso, turma.Ano_Form)}>Solicitar liberação</button>;
    default:
      return null;
  }
}

// Uma turma: faixa e fundo na cor do status, turma + status + vendedor na primeira linha
function TurmaLinha({ turma, liberacaoAprovada, children }) {
  const semCategoria = !turma.CategoriaStatus && turma.Status;
  return (
    <li className={`cx-turma-linha cx-st-${classeDoStatus(turma)}`}>
      <div className="cx-tl-topo">
        <span className="cx-tl-ano">{turma.Ano_Form}</span>
        <span className="cx-tl-status" title={semCategoria ? 'Etapa do Bitrix sem categoria no Mapa' : (turma.Status || '')}>{rotuloDoStatus(turma)}</span>
        {liberacaoAprovada && <span className="cx-selo cx-solicitado">Liberação aprovada</span>}
        <span className="cx-tl-vendedor">{nomeVendedor(turma.vendedor_id, turma.nome_vendedor || turma.vendedor)}</span>
      </div>
      {children}
    </li>
  );
}

export default function PainelCurso({ cursoBase, abrirRelacionar, foco, usuario, permissao, solicitacoes, onFechar, onOrcar, onSolicitar, onAvaliar, onProrrogar, onAlterado }) {
  const [curso, setCurso] = useState(cursoBase && cursoBase.turmas ? cursoBase : null);
  const [carregando, setCarregando] = useState(true);
  const [semStatus, setSemStatus] = useState(false);
  const [relacionando, setRelacionando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const botaoFechar = useRef(null);
  const abriuRelacionar = useRef(false);
  const secaoHistorico = useRef(null);
  const rolouHistorico = useRef(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    return api.buscarCursoCompleto(cursoBase)
      .then(resposta => {
        const encontrado = agruparCursos(resposta.data).find(c => c.id === String(cursoBase.id));
        if (encontrado) setCurso(encontrado);
        setSemStatus(!!resposta.statusIndisponivel);
      })
      .catch(() => toast.error('Não foi possível carregar as turmas deste curso.'))
      .then(() => setCarregando(false));
  }, [cursoBase]);

  useEffect(() => { carregar(); }, [carregar]);

  // A tela recarrega a cada 30 s: guardar o onFechar mais recente sem refazer o foco
  const fechar = useRef(onFechar);
  fechar.current = onFechar;
  useEffect(() => {
    if (botaoFechar.current) botaoFechar.current.focus();
    const aoTeclar = (e) => {
      // Esc fecha o painel só se não houver uma janela aberta por cima
      if (e.key === 'Escape' && !document.querySelector('.cx-janela')) fechar.current();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, []);

  useEffect(() => {
    if (abrirRelacionar && curso && !carregando && !abriuRelacionar.current) {
      abriuRelacionar.current = true;
      setRelacionando(true);
    }
  }, [abrirRelacionar, curso, carregando]);

  // Aberto pelo chip "Antes" da tabela: rola até o histórico assim que as turmas carregam
  useEffect(() => {
    if (foco === 'historico' && curso && !carregando && !rolouHistorico.current && secaoHistorico.current) {
      rolouHistorico.current = true;
      secaoHistorico.current.scrollIntoView({ block: 'start' });
    }
  }, [foco, curso, carregando]);

  const irParaHistorico = () => { if (secaoHistorico.current) secaoHistorico.current.scrollIntoView({ block: 'start', behavior: 'smooth' }); };

  const estender = async (turma) => {
    try {
      const resposta = await api.estenderPrazoContato(turma.id_curso_inst, turma.Ano_Form);
      if (resposta && resposta.novaDataFinal) {
        setCurso(atual => ({
          ...atual,
          turmas: atual.turmas.map(t => (t.Ano_Form === turma.Ano_Form ? { ...t, range_end_date: resposta.novaDataFinal } : t))
        }));
        const nova = new Date(resposta.novaDataFinal).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
        toast.success(`Prazo de contato de ${turma.Ano_Form} estendido até ${nova}.`);
      } else {
        toast.error('Ocorreu um erro ao receber os dados atualizados.');
      }
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.message) || 'Não foi possível estender o prazo de contato.');
    }
  };

  // Grava só nas turmas livres do ano atual em diante; o histórico não muda
  const relacionar = async (vendedor) => {
    const livres = turmasLivresParaRelacionar(curso);
    if (livres.length === 0) {
      toast.info(`Este curso não tem turma livre de ${new Date().getFullYear()} em diante registrada no banco: não há onde gravar o vendedor relacionado.`);
      setRelacionando(false);
      return;
    }
    setSalvando(true);
    const resultados = await Promise.all(livres.map(t =>
      api.gravarVendedorDaTurma(t.id_curso_inst, t.Ano_Form, vendedor).then(() => true, () => false)
    ));
    const gravadas = resultados.filter(Boolean).length;
    const falhas = resultados.length - gravadas;
    if (falhas === 0) toast.success(`Vendedor ${vendedor.nome} relacionado a ${gravadas} turma(s).`);
    else if (gravadas > 0) toast.warn(`${gravadas} turma(s) relacionada(s) a ${vendedor.nome}, mas ${falhas} não puderam ser gravada(s). Atualize a página e tente de novo.`);
    else toast.error('Não foi possível atualizar o vendedor do grupo.');
    setSalvando(false);
    setRelacionando(false);
    carregar();
    onAlterado();
  };

  const historico = curso ? historicoRecente(curso) : [];
  const turmasComRegistro = curso ? curso.turmas.filter(t => !t._placeholder) : [];
  const titulo = cursoBase.nomecurso || (curso && curso.nomecurso);
  const instituicao = cursoBase.nomeintituicao || (curso && curso.nomeintituicao);
  const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

  return (
    <React.Fragment>
      <div className="cx-fundo" onClick={onFechar} />
      <aside className="cx-painel" role="dialog" aria-modal="true" aria-label={`${titulo} — ${instituicao}`}>
        <header>
          <div className="cx-linha-titulo">
            <div style={{ minWidth: 0 }}>
              <h3>{titulo}</h3>
              {curso && (
                <div className="cx-sub" style={{ whiteSpace: 'normal' }}>
                  {curso.nomeintituicao}{curso.sigla_instituicao ? ` (${curso.sigla_instituicao})` : ''} · {curso.nomecidade}/{curso.siglaestado}
                </div>
              )}
            </div>
            <button type="button" className="cx-icone-btn" aria-label="Fechar" onClick={onFechar} ref={botaoFechar}>✕</button>
          </div>
          {curso && (
            <React.Fragment>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className={`cx-selo ${curso.vinculado ? 'cx-vinculado' : 'cx-livre'}`}>{curso.vinculado ? 'Vinculado' : 'Livre'}</span>
                <span className="cx-sub">Vendedor: <b style={{ color: 'var(--cx-fg)' }}>{nomeVendedor(curso.vendedor_id, curso.nome_vendedor)}</b></span>
                {historico.length > 0 && (
                  <button type="button" className="cx-link" onClick={irParaHistorico}>
                    Histórico: {plural(historico.length, 'turma', 'turmas')} antes de 2025 ↓
                  </button>
                )}
              </div>
              <div className="cx-acoes">
                {podeOrcar(curso, usuario, solicitacoes) && <button type="button" className="cx-btn cx-primario" onClick={() => onOrcar(curso)}>Orçar</button>}
                {turmasParaSolicitar(curso, usuario, solicitacoes, permissao).length > 0 && (
                  <button type="button" className="cx-btn" onClick={() => onSolicitar(curso, null)}>Solicitar liberação</button>
                )}
                {permissao !== 'ver' && <button type="button" className="cx-btn" onClick={() => setRelacionando(true)} disabled={carregando}>Relacionar vendedor</button>}
                {negocioMaisRecente(curso) && (
                  <a className="cx-btn" href={`${LINK_BITRIX}${negocioMaisRecente(curso).idNegocioBitrix}/`} target="_blank" rel="noopener noreferrer">Abrir negócio no Bitrix ↗</a>
                )}
              </div>
            </React.Fragment>
          )}
        </header>

        <div className="cx-corpo">
          {semStatus && <p className="cx-dica">Não foi possível consultar o Bitrix agora: os status podem aparecer em branco.</p>}
          {carregando && !curso && <div className="cx-carregando">Carregando turmas...</div>}
          {curso && (
            <React.Fragment>
              <section className="cx-secao">
                <div className="cx-secao-topo">
                  <h4>Turmas de 2025 em diante</h4>
                  <span className="cx-dica">{plural(turmasComRegistro.length, 'turma', 'turmas')}{carregando ? ' · atualizando...' : ''}</span>
                </div>
                {turmasComRegistro.length === 0 ? (
                  <p className="cx-dica cx-secao-vazia">Nenhuma turma deste curso tem registro de 2025 em diante.</p>
                ) : (
                  <ul className="cx-lista-turmas">
                    {turmasComRegistro.map(turma => {
                      const prazo = prazoContato(turma);
                      const estado = estadoTurma(curso, turma, usuario, solicitacoes, permissao);
                      const pode60 = podeEstenderPrazo(turma, usuario, solicitacoes);
                      // Vencido em vermelho só quando dá para estender; turma fechada não mostra prazo
                      const mostraPrazo = prazo && turma.CategoriaStatus !== 'FECHADOS';
                      const classePrazo = prazo && prazo.situacao === 'vencido' && !pode60 ? 'cx-prazo-neutro' : `cx-prazo-${prazo ? prazo.situacao : ''}`;
                      return (
                        <TurmaLinha key={turma.Ano_Form} turma={turma} liberacaoAprovada={temSolicitacaoAprovada(curso, turma, solicitacoes)}>
                          <div className="cx-tl-detalhes">
                            {mostraPrazo && <span>Contato até {prazo.texto} · <b className={classePrazo}>{prazo.rotulo}</b></span>}
                            {turma.data_inclusao_orcamento && <span>Orçamento em {new Date(turma.data_inclusao_orcamento).toLocaleDateString('pt-BR')}</span>}
                            {temNegocio(turma) && (
                              <a className="cx-link" href={`${LINK_BITRIX}${turma.idNegocioBitrix}/`} target="_blank" rel="noopener noreferrer">Negócio {turma.idNegocioBitrix} ↗</a>
                            )}
                          </div>
                          {(estado.tipo !== 'nada' || pode60) && (
                            <div className="cx-tl-acoes">
                              <AcaoDaTurma estado={estado} curso={curso} turma={turma} onSolicitar={onSolicitar} onAvaliar={onAvaliar} onProrrogar={onProrrogar} />
                              {pode60 && <button type="button" className="cx-btn cx-mini" onClick={() => estender(turma)}>+60 dias de contato</button>}
                            </div>
                          )}
                        </TurmaLinha>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section className="cx-secao" ref={secaoHistorico}>
                <div className="cx-secao-topo">
                  <h4>Histórico · turmas até 2024</h4>
                  <span className="cx-dica">{plural(historico.length, 'turma', 'turmas')}</span>
                </div>
                {historico.length === 0 ? (
                  <p className="cx-dica cx-secao-vazia">Este curso não tem turmas com negócio antes de 2025.</p>
                ) : (
                  <ul className="cx-lista-turmas">
                    {historico.map(item => <TurmaLinha key={item.Ano_Form} turma={item} />)}
                  </ul>
                )}
              </section>
            </React.Fragment>
          )}
        </div>
      </aside>

      {relacionando && curso && (
        <DialogoRelacionar
          curso={curso}
          turmasLivres={turmasLivresParaRelacionar(curso)}
          salvando={salvando}
          onEscolher={relacionar}
          onFechar={() => { if (!salvando) setRelacionando(false); }}
        />
      )}
    </React.Fragment>
  );
}
